import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { statusMeta } from './order-enums';
import { useOrderLogs } from './orders.api';

/**
 * The order's status history, opened from the Logs button on its row.
 *
 * The original put this in an antd Popover anchored to the button, which on a
 * phone opened a table wider than the screen with no way to scroll it. A dialog
 * has room, and the log is rendered as a timeline because that is what it is:
 * a sequence of status transitions, each with a before and an after.
 */
export function OrderLogsDialog({
  orderId,
  confirmationCode,
  open,
  onOpenChange,
}: {
  orderId: string | null;
  confirmationCode: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const { data, isLoading, isFetching, isError } = useOrderLogs(
    open ? orderId : null
  );
  // A log already on screen being re-read — reopening a row whose cache went
  // stale.
  const busy = isFetching && !isLoading;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-black/45 backdrop-blur-[2px]',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0'
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'bg-background fixed top-1/2 left-1/2 z-50 flex max-h-[85svh] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col rounded-xl border shadow-xl',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95'
          )}
        >
          <div className="flex items-start justify-between gap-4 border-b p-4">
            <div>
              <DialogPrimitive.Title className="font-semibold">
                {t('orders.logs')}
                {data && (
                  <span className="text-muted-foreground ml-1.5 font-normal tabular-nums">
                    ({data.length})
                  </span>
                )}
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="text-muted-foreground font-mono text-xs">
                {confirmationCode ?? ''}
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close className="text-muted-foreground hover:text-foreground focus-visible:ring-ring rounded p-1 focus-visible:ring-2 focus-visible:outline-none">
              <X className="size-4" />
              <span className="sr-only">{t('common.cancel')}</span>
            </DialogPrimitive.Close>
          </div>

          {/* `relative` outside the scroll box, so the busy spinner is not
              scrolled away with the entries. */}
          <div className="relative flex min-h-0 flex-col rounded-b-xl">
            <BusyOverlay show={busy} />
            <div
              className={cn(
                'overflow-y-auto p-4',
                busy && 'opacity-60 transition-opacity'
              )}
            >
              {/* `isLoading`, not `isPending`: the query is disabled while the
                  dialog is closed, and pending forever then. */}
              {isLoading && (
                <ol className="space-y-4 pl-5">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <li key={index} className="space-y-1.5">
                      <div className="flex items-center gap-1.5">
                        <Skeleton className="h-5 w-20 rounded-full" />
                        <Skeleton className="h-5 w-20 rounded-full" />
                      </div>
                      <Skeleton className="h-3 w-32" />
                    </li>
                  ))}
                </ol>
              )}

              {isError && (
                <p className="text-muted-foreground py-10 text-center text-sm">
                  {t('orders.logsFailed')}
                </p>
              )}

              {data?.length === 0 && (
                <p className="text-muted-foreground py-10 text-center text-sm">
                  {t('orders.noLogs')}
                </p>
              )}

              {data && data.length > 0 && (
                <ol className="relative space-y-4 pl-5">
                  <span
                    aria-hidden
                    className="bg-border absolute top-2 bottom-2 left-[3px] w-px"
                  />
                  {data.map((log) => {
                    const before = statusMeta(log.preStatus);
                    const after = statusMeta(log.postStatus);
                    return (
                      <li key={log.id} className="relative">
                        <span
                          aria-hidden
                          className="bg-border ring-background absolute top-1.5 -left-5 size-[7px] rounded-full ring-4"
                        />
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Badge variant="outline" className="opacity-70">
                            {t(before.labelKey)}
                          </Badge>
                          <span className="text-muted-foreground text-xs">→</span>
                          <Badge variant={after.tone}>{t(after.labelKey)}</Badge>
                        </div>
                        <p className="text-muted-foreground mt-1 text-xs">
                          {formatDateTime(log.modifiedDate ?? log.createdAt)}
                        </p>
                        {log.description && (
                          <p className="mt-0.5 text-sm text-pretty">
                            {log.description}
                          </p>
                        )}
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
